import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Editor from '../components/CodeEditor';
import { getErrorMessage, submissionService } from '../services/api';
import type { SharedSubmission } from '../types/api';
import MetricsDisplay from '../components/MetricsDisplay';
import StatusBadge from '../components/StatusBadge';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Code,
  GraduationCap,
  LayoutDashboard,
  LogIn,
  MessageSquare,
} from 'lucide-react';
import AuthShell from '../components/AuthShell';
import ThemeToggle from '../components/ThemeToggle';
import { LoadingState } from '../components/ui';
import { formatDateTime } from '../utils/format';

const SubmissionView: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [submission, setSubmission] = useState<SharedSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const monacoTheme = isDarkMode ? 'vs-dark' : 'vs';

  useEffect(() => {
    if (!shareToken) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    submissionService.getShared(shareToken)
      .then((data) => { if (!cancelled) setSubmission(data); })
      .catch((err) => { if (!cancelled) setError(getErrorMessage(err, 'Submission not found')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [shareToken]);

  if (loading) {
    return <LoadingState label="Loading submission..." className="min-h-screen" />;
  }

  if (error || !submission) {
    return (
      <AuthShell brandAsHeading={false}>
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Submission Not Found</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <button onClick={() => navigate('/login')} className="btn btn-primary btn-lg w-full sm:w-auto">
            Go to Login
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <div className="page-shell">
      <nav className="bg-white/90 dark:bg-gray-900/90 backdrop-blur border-b border-gray-200 dark:border-gray-800 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex justify-between items-center gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white shadow-sm">
              <GraduationCap className="w-5 h-5" />
            </span>
            <span className="min-w-0 leading-tight">
              <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Webots Academy</span>
              <span className="block text-base font-semibold text-gray-900 dark:text-white truncate">Shared Submission</span>
            </span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3">
            <ThemeToggle />
            <button onClick={() => navigate(user ? '/dashboard' : '/login')} className="btn btn-secondary">
              {user ? <LayoutDashboard /> : <LogIn />}
              <span>{user ? 'Dashboard' : 'Login'}</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-3 mb-1">
            <h1 className="page-title">Code Submission</h1>
            <StatusBadge status={submission.status} />
          </div>
          <p className="page-subtitle">
            Submitted on {formatDateTime(submission.submitted_at)}
            {/* The backend stores 0 when nothing was measured */}
            {!!submission.execution_time && <> · Execution time: {submission.execution_time}ms</>}
          </p>
        </div>

        {submission.teacher_review && (
          <div className="mb-6 p-4 sm:p-5 bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-900/60 rounded-xl">
            <h2 className="flex items-center gap-2 font-semibold text-violet-900 dark:text-violet-200 mb-2">
              <MessageSquare className="w-5 h-5" /> Teacher Review
            </h2>
            <p className="text-violet-900/90 dark:text-violet-100 whitespace-pre-wrap leading-relaxed">{submission.teacher_review}</p>
            {submission.reviewed_at && (
              <p className="text-sm text-violet-700 dark:text-violet-300 mt-2">
                Reviewed on {formatDateTime(submission.reviewed_at)}
              </p>
            )}
          </div>
        )}

        {submission.metrics && typeof submission.metrics === 'object' && Object.keys(submission.metrics).length > 0 && (
          <MetricsDisplay metrics={submission.metrics} title="Simulation Metrics" className="mb-6" />
        )}

        <div className="card overflow-hidden">
          <h2 className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-800">
            <Code className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <span>Submitted Code</span>
          </h2>
          <div style={{ height: '500px' }}>
            <Editor
              height="100%"
              defaultLanguage="python"
              value={submission.code}
              theme={monacoTheme}
              options={{
                readOnly: true,
                minimap: { enabled: false },
                fontSize: 13,
                padding: { top: 8, bottom: 8 },
                scrollBeyondLastLine: false,
                renderLineHighlight: 'all',
                lineNumbersMinChars: 3,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionView;
